import * as React from 'react';

import type { PickedChild, PickerQuery, PickerSelection } from './types';

/** "All matching": every row the query matches, minus the unticked ones. */
interface AllMatching {
  /** The query it was chosen for; another query leaves the mode (see `PickerBody`). */
  key: string;
  query: Omit<PickerQuery, 'sort'>;
  total: number;
  /** Matching rows unticked since, in the order they were unticked. */
  excluded: string[];
}

interface State<TItem, TChild> {
  /**
   * Explicit picks. `null` marks an id whose row has not loaded (initial or pasted). Insertion
   * order is pick order. They outlive "all matching".
   */
  items: Map<string, TItem | null>;
  /** Variant mode, keyed by child id. */
  children: Map<string, PickedChild<TItem, TChild> | null>;
  allMatching: AllMatching | null;
}

export interface PickerSelectionOptions {
  /** `false` for one pick (replaces), a number to cap, `true` for any. */
  multiple: boolean | number;
  /** The selection is made of children (variants). */
  variantMode: boolean;
  initialIds?: readonly string[] | undefined;
  initialChildIds?: readonly string[] | undefined;
  /**
   * Ids of the rows the list has loaded for the current query, in list order. In "all matching"
   * they are inside the query: explicit picks among them are covered, the rest become
   * `includeIds`.
   */
  loadedIds?: ReadonlySet<string>;
}

export interface UsePickerSelectionResult<TItem, TChild> {
  /** Items in item mode, children in variant mode; `total - excluded + included` in all matching. */
  count: number;
  max: number | undefined;
  /** A cap is set and reached: unpicked rows are locked. */
  full: boolean;
  /** A list row is ticked. In "all matching" any loaded row that was not unticked is. */
  isSelected: (id: string) => boolean;
  isChildSelected: (childId: string) => boolean;
  toggle: (id: string, item: TItem, checked: boolean) => void;
  /** The page checkbox: ticks or unticks the given (loaded, selectable) rows together. */
  togglePage: (rows: ReadonlyArray<{ id: string; item: TItem }>, checked: boolean) => void;
  /** Ticks or unticks children of one item together (a parent checkbox, or a lone child). */
  toggleChildren: (
    itemId: string,
    item: TItem,
    children: ReadonlyArray<{ id: string; child: TChild }>,
    checked: boolean,
  ) => void;
  /** Adds ids whose rows may not have loaded (paste, a source); returns how many were new. */
  addIds: (ids: readonly string[]) => number;
  /** Drops explicit picks (a remove button on the Selected tab). */
  removeIds: (ids: readonly string[]) => void;
  /** Ticks an unticked matching row again. */
  unexclude: (id: string) => void;
  allMatching: { key: string; query: Omit<PickerQuery, 'sort'>; total: number } | null;
  selectAllMatching: (next: {
    key: string;
    query: Omit<PickerQuery, 'sort'>;
    total: number;
  }) => void;
  /** Back to id mode; explicit picks stay. */
  leaveAllMatching: () => void;
  /** Drops everything, explicit picks included. */
  clear: () => void;
  /** Explicit picks in pick order, `null` where the row has not loaded. */
  picked: ReadonlyArray<readonly [string, TItem | null]>;
  /** Variant mode: chosen children in pick order, `null` where the row has not loaded. */
  pickedChildren: ReadonlyArray<readonly [string, PickedChild<TItem, TChild> | null]>;
  /** All matching: explicit picks outside the loaded rows of the query. */
  includeIds: string[];
  /** All matching: the unticked matching rows. */
  excludeIds: string[];
  /**
   * The selection to hand back. `loaded` fills ids from rows that have loaded since; in variant
   * mode it also returns the loaded children of an item, for initial child ids.
   */
  snapshot: (loaded: {
    item: (id: string) => TItem | undefined;
    children: (childId: string) => PickedChild<TItem, TChild> | undefined;
    sort: string | undefined;
  }) => PickerSelection<TItem, TChild>;
}

const NO_IDS: ReadonlySet<string> = new Set();

/** Selection state of one open picker. */
export function usePickerSelection<TItem, TChild = never>({
  multiple,
  variantMode,
  initialIds = [],
  initialChildIds = [],
  loadedIds = NO_IDS,
}: PickerSelectionOptions): UsePickerSelectionResult<TItem, TChild> {
  const [state, setState] = React.useState<State<TItem, TChild>>(() => ({
    items: new Map(initialIds.map((id) => [id, null])),
    children: new Map(initialChildIds.map((id) => [id, null])),
    allMatching: null,
  }));
  // Mirrors the latest state, so `addIds` can answer synchronously and two updates in one event
  // build on each other.
  const latest = React.useRef(state);
  const loadedRef = React.useRef(loadedIds);
  React.useEffect(() => {
    loadedRef.current = loadedIds;
  });
  const update = React.useCallback((next: (prev: State<TItem, TChild>) => State<TItem, TChild>) => {
    const resolved = next(latest.current);
    if (resolved === latest.current) return;
    latest.current = resolved;
    setState(resolved);
  }, []);

  const all = state.allMatching;
  const excludedSet = React.useMemo(() => new Set(all?.excluded ?? []), [all]);
  const includeIds = all ? [...state.items.keys()].filter((id) => !loadedIds.has(id)) : [];
  const count = variantMode
    ? state.children.size
    : all
      ? Math.max(all.total - all.excluded.length, 0) + includeIds.length
      : state.items.size;
  const max = typeof multiple === 'number' ? multiple : undefined;
  const full = all === null && max !== undefined && count >= max;

  const toggle = React.useCallback(
    (id: string, item: TItem, checked: boolean) => {
      update((prev) => {
        if (prev.allMatching) {
          const items = new Map(prev.items);
          const rest = prev.allMatching.excluded.filter((other) => other !== id);
          if (checked) items.set(id, item);
          else items.delete(id);
          // Only a row inside the query is excluded; an explicit pick outside it just goes.
          const excluded = !checked && loadedRef.current.has(id) ? [...rest, id] : rest;
          return { ...prev, items, allMatching: { ...prev.allMatching, excluded } };
        }
        const items = multiple === false ? new Map<string, TItem | null>() : new Map(prev.items);
        if (checked) {
          if (typeof multiple === 'number' && !prev.items.has(id) && prev.items.size >= multiple) {
            return prev;
          }
          items.set(id, item);
        } else {
          items.delete(id);
        }
        return { ...prev, items };
      });
    },
    [multiple, update],
  );

  const togglePage = React.useCallback(
    (rows: ReadonlyArray<{ id: string; item: TItem }>, checked: boolean) => {
      update((prev) => {
        const items = new Map(prev.items);
        if (prev.allMatching) {
          if (checked) {
            const ids = new Set(rows.map((row) => row.id));
            const excluded = prev.allMatching.excluded.filter((id) => !ids.has(id));
            return { ...prev, allMatching: { ...prev.allMatching, excluded } };
          }
          // Unticking the page while all match deselects them all; explicit picks off the page stay.
          for (const row of rows) items.delete(row.id);
          return { ...prev, items, allMatching: null };
        }
        if (!checked) {
          for (const row of rows) items.delete(row.id);
          return { ...prev, items };
        }
        let room =
          typeof multiple === 'number'
            ? Math.max(multiple - items.size, 0)
            : Number.POSITIVE_INFINITY;
        for (const row of rows) {
          if (items.has(row.id)) {
            items.set(row.id, row.item);
            continue;
          }
          if (room <= 0) break;
          items.set(row.id, row.item);
          room -= 1;
        }
        return { ...prev, items };
      });
    },
    [multiple, update],
  );

  const toggleChildren = React.useCallback(
    (
      itemId: string,
      item: TItem,
      picked: ReadonlyArray<{ id: string; child: TChild }>,
      checked: boolean,
    ) => {
      update((prev) => {
        if (!checked) {
          const children = new Map(prev.children);
          for (const { id } of picked) children.delete(id);
          return { ...prev, children };
        }
        const children =
          multiple === false
            ? new Map<string, PickedChild<TItem, TChild> | null>()
            : new Map(prev.children);
        // One pick takes a single child; a cap takes as many as still fit, in list order.
        const room =
          multiple === false
            ? 1
            : typeof multiple === 'number'
              ? Math.max(multiple - children.size, 0)
              : Number.POSITIVE_INFINITY;
        let added = 0;
        for (const { id, child } of picked) {
          if (children.has(id)) {
            children.set(id, { id, itemId, item, child });
            continue;
          }
          if (added >= room) break;
          children.set(id, { id, itemId, item, child });
          added += 1;
        }
        return { ...prev, children };
      });
    },
    [multiple, update],
  );

  const addIds = React.useCallback(
    (ids: readonly string[]) => {
      let added = 0;
      update((prev) => {
        const items = new Map(prev.items);
        const unique = [...new Set(ids)];
        let room =
          prev.allMatching || typeof multiple !== 'number'
            ? Number.POSITIVE_INFINITY
            : Math.max(multiple - items.size, 0);
        for (const id of unique) {
          if (items.has(id)) continue;
          if (room <= 0) break;
          items.set(id, null);
          room -= 1;
          added += 1;
        }
        const allMatching = prev.allMatching
          ? {
              ...prev.allMatching,
              excluded: prev.allMatching.excluded.filter((id) => !unique.includes(id)),
            }
          : null;
        return { ...prev, items, allMatching };
      });
      return added;
    },
    [multiple, update],
  );

  const removeIds = React.useCallback(
    (ids: readonly string[]) => {
      update((prev) => {
        const items = new Map(prev.items);
        for (const id of ids) items.delete(id);
        return { ...prev, items };
      });
    },
    [update],
  );

  const unexclude = React.useCallback(
    (id: string) => {
      update((prev) =>
        prev.allMatching
          ? {
              ...prev,
              allMatching: {
                ...prev.allMatching,
                excluded: prev.allMatching.excluded.filter((other) => other !== id),
              },
            }
          : prev,
      );
    },
    [update],
  );

  const selectAllMatching = React.useCallback<
    UsePickerSelectionResult<TItem, TChild>['selectAllMatching']
  >(
    (next) => {
      update((prev) => ({ ...prev, allMatching: { ...next, excluded: [] } }));
    },
    [update],
  );

  const leaveAllMatching = React.useCallback(() => {
    update((prev) => (prev.allMatching ? { ...prev, allMatching: null } : prev));
  }, [update]);

  const clear = React.useCallback(() => {
    update((prev) => ({ ...prev, items: new Map(), children: new Map(), allMatching: null }));
  }, [update]);

  const snapshot: UsePickerSelectionResult<TItem, TChild>['snapshot'] = (loaded) => {
    const resolveItems = (ids: readonly string[]) => {
      const items: TItem[] = [];
      for (const id of ids) {
        const resolved = state.items.get(id) ?? loaded.item(id);
        if (resolved !== undefined && resolved !== null) items.push(resolved);
      }
      return items;
    };
    if (all && !variantMode) {
      const inside = [...loadedIds].filter((id) => !excludedSet.has(id));
      const ids = [...inside, ...includeIds];
      return {
        mode: 'query',
        ids,
        items: resolveItems(ids),
        childIds: [],
        children: [],
        query: {
          ...all.query,
          sort: loaded.sort,
          includeIds,
          excludeIds: all.excluded,
          count,
          total: all.total,
        },
      };
    }
    const children: Array<PickedChild<TItem, TChild>> = [];
    for (const [id, picked] of state.children) {
      const resolved = picked ?? loaded.children(id);
      if (resolved) children.push(resolved);
    }
    if (!variantMode) {
      const ids = [...state.items.keys()];
      return { mode: 'ids', ids, items: resolveItems(ids), childIds: [], children };
    }
    // Variant mode: the parents of the chosen children, in the order they were first picked.
    const parents = new Map<string, TItem>();
    for (const picked of children) {
      if (!parents.has(picked.itemId)) parents.set(picked.itemId, picked.item);
    }
    return {
      mode: 'ids',
      ids: [...parents.keys()],
      items: [...parents.values()],
      childIds: [...state.children.keys()],
      children,
    };
  };

  return {
    count,
    max,
    full,
    isSelected: (id) => state.items.has(id) || (all !== null && !excludedSet.has(id)),
    isChildSelected: (childId) => state.children.has(childId),
    toggle,
    togglePage,
    toggleChildren,
    addIds,
    removeIds,
    unexclude,
    allMatching: all ? { key: all.key, query: all.query, total: all.total } : null,
    selectAllMatching,
    leaveAllMatching,
    clear,
    picked: [...state.items.entries()],
    pickedChildren: [...state.children.entries()],
    includeIds,
    excludeIds: all?.excluded ?? [],
    snapshot,
  };
}
